import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Snowflake,
  Home,
  Building2,
  Laptop,
  Store,
  CheckCircle2,
  Shield,
  Clock,
  FileText,
  Zap,
  Paintbrush,
  CalendarCheck,
  MapPin,
  BadgeCheck,
} from 'lucide-react';
import PageLayout from '../PageLayout';
import Seo from '../Seo';
import FAQComponent from '../FAQComponent';
import CTABlock from '../CTABlock';
import {
  graph,
  businessSchema,
  breadcrumbSchema,
  serviceSchema,
  faqSchema,
} from '../../seo/site';
import '../ServicePage.css';

// NOTE: tier prices below are guide prices for the core catchment (RM13),
// calibrated to the strategy brief's modelled bands. Validate against the
// first ten real quotes and adjust.
const TIERS = [
  {
    name: 'Essential',
    brand: 'Reliable entry-level system',
    price: 'from £1,800',
    desc: 'One room, done properly. A quiet, reliable wall unit with professional installation and the walls made good afterwards.',
    features: ['One indoor and one outdoor unit', 'Up to 5m pipe run included', 'Electrical connection by our own electrician', 'Making good and full clean-down', '5-year manufacturer warranty with annual servicing'],
  },
  {
    name: 'Comfort',
    brand: 'Higher efficiency mid-range system',
    price: 'from £2,400',
    desc: 'The most popular choice. Quieter running, better efficiency and app control, specced properly for the room it serves.',
    features: ['Everything in Essential', 'Wi-Fi app control', 'Higher energy efficiency rating', 'Extended pipe runs and condensate solutions', 'Optional invisible chasing of pipework'],
  },
  {
    name: 'Premium',
    brand: 'Designer system, premium finish',
    price: 'from £3,000',
    desc: 'The design option. Whisper quiet designer wall units with the strongest warranty support in the industry.',
    features: ['Everything in Comfort', 'Designer wall unit range', 'Quietest operation in class', 'Multi-room configurations available', 'Extended warranty support with annual servicing'],
  },
];

const AC_FAQS = [
  {
    question: 'How much does air conditioning cost in Rainham and Havering?',
    answer: 'A single room installation starts from around £1,800 for an entry level system, £2,400 for a mid range unit and £3,000 for a designer unit. That price includes the installation, the electrical connection and the making good. Multi-room systems typically cost between £3,000 and £9,000. Every job is confirmed with a fixed written price after survey.',
  },
  {
    question: 'Do I need planning permission for air conditioning?',
    answer: 'Standalone air conditioning has no automatic permitted development rights. In many cases permission is not required where the installation would not materially affect the external appearance of your property, for example a discreet unit on a rear wall. Air-to-air heat pumps, which are broadly the same equipment, do have permitted development rights subject to the MCS 020a noise standard and siting conditions. Homes in Rainham\'s conservation area, and listed buildings, get a heritage screen as standard. We confirm the position for your address in writing with every quote.',
  },
  {
    question: 'Is air conditioning expensive to run?',
    answer: 'Running costs depend on the unit, the room and how you use it. As a guide, a modern 2.5kW bedroom unit typically draws roughly 0.6 to 0.9kW while cooling. With every quote we give you an estimated running cost range in writing, based on the unit\'s rated consumption and your tariff, rather than a single made up figure.',
  },
  {
    question: 'How long does air conditioning installation take?',
    answer: 'A single room installation is usually completed in one day, including the electrical connection and the making good. Multi-room systems typically take one to two days. Because our own electricians and plasterers do the finishing work, there is no waiting on extra trades.',
  },
  {
    question: 'Which brand of air conditioning do you install?',
    answer: 'We install a range of leading brands, including Daikin and Mitsubishi Electric, and recommend the unit that fits your budget, the room and how you plan to use it. Every quote sets out the options across the tiers in writing, so you choose with the full picture rather than a guess.',
  },
  {
    question: 'Can you install air conditioning in a loft room?',
    answer: 'Yes. Loft rooms are one of the most common installations we do. Insulation traps heat, so loft conversions can become unusable in summer. A correctly sized wall unit transforms the room, and loft jobs often suit a two unit configuration covering a bedroom as well.',
  },
  {
    question: 'Do you service what you install?',
    answer: 'Yes. Manufacturer warranties of up to ten years are conditional on annual servicing, so we offer a service plan priced per unit per year, carried out by F-Gas certified engineers. We book your first service at handover and remind you automatically each year. Commercial sites can be serviced two or three times a year.',
  },
];

const AirConditioningPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Seo
        path="/air-conditioning"
        title="Air Conditioning Installation Rainham, Havering & Essex | Fallow"
        description="Air conditioning installed across Rainham, Havering and Essex, with leading brands matched to your home and budget. F-Gas certified engineers, written quote within 1 hour of survey. Call 0203 576 5962."
        jsonLd={graph([
          businessSchema(),
          serviceSchema({
            name: 'Air conditioning installation',
            description:
              'Domestic and commercial air conditioning installation across Rainham, Havering and East London. Leading brands matched to each home and budget, with in-house electrics, walls made good, and F-Gas certified refrigerant work.',
            path: '/air-conditioning',
          }),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Air Conditioning', path: '/air-conditioning' },
          ]),
          faqSchema(AC_FAQS),
        ])}
      />

      <PageLayout
        heroTitle="Air Conditioning Installation in Rainham & Havering"
        heroSubtitle="Cooling designed around your home, your budget and how you live. Written quote within one hour of your survey."
        heroImage="/images/hero-construction.jpg"
      >
        {/* Overview + differentiator */}
        <section className="service-benefits">
          <div className="service-container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-subtitle">Overview</span>
              <h2 className="section-title">Air Conditioning That Puts Your Home First</h2>
            </motion.div>

            <motion.div
              className="content-block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p>Fallow Building Services installs air conditioning across Rainham, Havering and Essex. Every installation is planned around your home: the right unit for the room, the right system for your budget, and a finish that looks considered rather than added on. Our own electrician connects your system, F-Gas certified engineers complete the refrigerant work, and our plasterers and decorators make the walls good before we leave. You get a finished room, not a job half done.</p>
              <p style={{ marginTop: '16px' }}>We install a range of leading brands, including Daikin and Mitsubishi Electric, so the system fits your budget and situation rather than the other way round. Surveys are booked for the next working day, and your written quotation arrives within one hour of the survey. Call <a href="tel:02035765962" className="contact-link">0203 576 5962</a> to book yours.</p>
            </motion.div>
          </div>
        </section>

        {/* Who it is for: the four RM13 demand drivers */}
        <section className="service-benefits">
          <div className="service-container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-subtitle">Who It&rsquo;s For</span>
              <h2 className="section-title">The Rooms That Need Cooling Most</h2>
            </motion.div>

            <motion.div
              className="benefits-grid-modern"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {[
                { icon: Home, title: 'Loft Rooms', desc: 'Loft conversions trap heat and can be unusable from June to September. A correctly sized wall unit transforms the room. These are the most common installations we do, and they often suit a second unit covering the bedroom below.' },
                { icon: Building2, title: 'Extensions & Kitchen-Diners', desc: 'A glazed extension is expensive to heat and uncomfortable in August. A discreet unit makes it usable all year. As builders, we position units and pipework so the room still looks finished.' },
                { icon: Laptop, title: 'Home Offices', desc: 'Working from home should not mean sweating through video calls. Send us photos of the room and we can often quote the same day, without a visit.' },
                { icon: Store, title: 'Commercial Spaces', desc: 'Offices, salons, gyms, restaurants and server rooms along the A13 corridor. Systems that run all day, serviced up to three times a year. See our commercial maintenance services.' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    className="benefit-card-modern"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    whileHover={{ y: -4 }}
                  >
                    <div className="benefit-icon-wrapper">
                      <Icon size={32} />
                    </div>
                    <h3 className="benefit-title">{item.title}</h3>
                    <p className="benefit-description">{item.desc}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* The offer: named, tiered */}
        <section className="service-benefits">
          <div className="service-container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-subtitle">The Offer</span>
              <h2 className="section-title">Three Tiers, Leading Brands, One Fixed Price</h2>
            </motion.div>

            <motion.div
              className="benefits-grid-modern"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {TIERS.map((tier, index) => (
                <motion.div
                  key={tier.name}
                  className="benefit-card-modern"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  whileHover={{ y: -4 }}
                >
                  <div className="benefit-icon-wrapper">
                    <Snowflake size={32} />
                  </div>
                  <h3 className="benefit-title">{tier.name}: {tier.price}</h3>
                  <p className="benefit-description" style={{ fontWeight: 600 }}>{tier.brand}</p>
                  <p className="benefit-description">{tier.desc}</p>
                  <ul style={{ textAlign: 'left', marginTop: '12px', paddingLeft: '20px' }}>
                    {tier.features.map((feature) => (
                      <li key={feature} style={{ marginBottom: '6px' }}>{feature}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              className="content-block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <p style={{ marginTop: '24px' }}>Prices are guide prices for a standard single room installation in our core area. Every job is confirmed with a fixed written price after survey. Multi-room systems typically run £3,000 to £9,000 depending on the number of units and the configuration. If the budget is tighter, we show you a lower tier rather than discount the work.</p>
              <p style={{ marginTop: '16px' }}><strong>Ready for a price on your room?</strong> Call <a href="tel:02035765962" className="contact-link">0203 576 5962</a> or <Link to="/contact" className="contact-link">send us photos for a same day quote</Link>.</p>
            </motion.div>
          </div>
        </section>

        {/* Planning truth: the differentiator */}
        <section className="service-benefits">
          <div className="service-container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-subtitle">Planning &amp; Permissions</span>
              <h2 className="section-title">Do You Need Planning Permission for Air Conditioning?</h2>
            </motion.div>

            <motion.div
              className="content-block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p>This is the question most installers answer badly, and in a borough with a conservation area a wrong answer can cost you a retrospective application. The accurate position: standalone air conditioning has no automatic permitted development rights. In many cases permission is not needed, for example a discreet unit on a rear wall that does not materially change how your property looks. Air-to-air heat pumps, which are broadly the same physical equipment, do have permitted development rights subject to the MCS 020a noise standard and siting conditions.</p>
              <p style={{ marginTop: '16px' }}>Homes in Rainham&rsquo;s conservation area, and listed buildings, are screened as a standard part of our survey. <strong>We confirm the position for your address in writing with every quote.</strong> If paperwork is needed, we lodge it.</p>
            </motion.div>
          </div>
        </section>

        {/* Process */}
        <section className="service-benefits">
          <div className="service-container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-subtitle">Our Process</span>
              <h2 className="section-title">From Survey to Finished Room in Four Steps</h2>
            </motion.div>

            <motion.div
              className="benefits-grid-modern"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {[
                { icon: CalendarCheck, title: '1. Survey', desc: 'Booked for the next working day. One or two units can often be quoted remotely from photos and a video call. Larger jobs get a physical visit. Every survey includes the planning and conservation area screen.' },
                { icon: FileText, title: '2. Quote Within 1 Hour', desc: 'Three tiered options across our leading brand range arrive in writing within one hour of the survey, so you choose a tier rather than guess a budget. Approve and pay the deposit online.' },
                { icon: Zap, title: '3. Install', desc: 'Our own electrician connects the system and F-Gas certified engineers complete all refrigerant work. Most single room installations are finished in one day.' },
                { icon: Paintbrush, title: '4. Handover', desc: 'Walls made good, room cleaned down, full demonstration and your documentation pack. Your first annual service is booked before we leave, so your warranty stays protected.' },
              ].map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    className="benefit-card-modern"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    whileHover={{ y: -4 }}
                  >
                    <div className="benefit-icon-wrapper">
                      <Icon size={32} />
                    </div>
                    <h3 className="benefit-title">{step.title}</h3>
                    <p className="benefit-description">{step.desc}</p>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div
              className="content-block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <p style={{ marginTop: '24px', textAlign: 'center' }}><strong>Most surveys happen the next working day.</strong> <Link to="/contact" className="contact-link">Book yours now</Link>.</p>
            </motion.div>
          </div>
        </section>

        {/* Service plan: the annuity */}
        <section className="service-benefits">
          <div className="service-container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-subtitle">Aftercare</span>
              <h2 className="section-title">The Annual Service Plan</h2>
            </motion.div>

            <motion.div
              className="content-block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p>Manufacturer warranties of up to ten years are conditional on annual servicing. Our service plan is priced per unit per year and carried out by F-Gas certified engineers, with automatic reminders, so your warranty stays intact without you having to remember anything. Your first service is booked at handover.</p>
              <p style={{ marginTop: '16px' }}>For commercial sites such as offices, gyms, salons, restaurants, care homes and server rooms, we service systems two or three times a year under one agreement, with F-Gas records maintained for your compliance file. See our <Link to="/maintenance" className="contact-link">property maintenance services</Link>.</p>
            </motion.div>
          </div>
        </section>

        {/* Why Fallow */}
        <section className="service-benefits">
          <div className="service-container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-subtitle">Why Fallow</span>
              <h2 className="section-title">What Every Installation Includes</h2>
            </motion.div>

            <motion.div
              className="benefits-grid-modern"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {[
                { icon: BadgeCheck, title: 'F-Gas Certified Engineers', desc: 'All refrigerant work is carried out by F-Gas certified engineers, with handling records kept for every job. Your warranty and compliance file stay intact.' },
                { icon: Zap, title: 'Our Own Electrician', desc: 'Every electrical connection is done in-house to Part P standard. No waiting on third parties for first or second fix.' },
                { icon: Paintbrush, title: 'Walls Made Good', desc: 'Chasing, plastering and decoration by our own trades. We hand over a finished room, not a building site.' },
                { icon: Shield, title: '12-Month Workmanship Guarantee', desc: 'A written commitment on every installation. If anything needs attention in the first year, we return and put it right at no cost.' },
                { icon: Clock, title: 'Quote Within 1 Hour', desc: 'A stated service level. Surveys are booked for the next working day and the written quotation follows within one hour.' },
                { icon: FileText, title: 'Planning Confirmed In Writing', desc: 'The accurate position for your address, in writing, with every quote. Conservation area screen included.' },
                { icon: CheckCircle2, title: 'Brands Matched To Your Budget', desc: 'We install a range of leading brands, including Daikin and Mitsubishi Electric, and recommend the one that fits your budget and situation. The right unit for your home, never the easiest sale.' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    className="benefit-card-modern"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.08 * index }}
                    whileHover={{ y: -4 }}
                  >
                    <div className="benefit-icon-wrapper">
                      <Icon size={28} />
                    </div>
                    <h3 className="benefit-title">{item.title}</h3>
                    <p className="benefit-description">{item.desc}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* FAQs */}
        <section className="service-benefits">
          <div className="service-container">
            <FAQComponent title="Air Conditioning FAQs" faqs={AC_FAQS} />
          </div>
        </section>

        {/* Areas */}
        <section className="service-areas-section">
          <div className="service-container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-subtitle">Service Area</span>
              <h2 className="section-title">Where We Install</h2>
            </motion.div>

            <motion.div
              className="content-block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p>We are based in Rainham and install air conditioning across the London Borough of Havering and the surrounding areas. Our core area is RM13, covering Rainham, South Hornchurch and Wennington, with regular work across Romford, Hornchurch, Upminster, Emerson Park, Elm Park and Gidea Park, and commercial installations along the A13 and Thames Gateway corridor.</p>
            </motion.div>

            <motion.div
              className="service-areas-grid"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {['Rainham', 'South Hornchurch', 'Wennington', 'Romford', 'Hornchurch', 'Upminster', 'Emerson Park', 'Elm Park', 'Gidea Park', 'Barking & Dagenham'].map((area, index) => (
                <motion.div
                  key={area}
                  className="service-area-card"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.05 * index }}
                >
                  <MapPin size={18} />
                  <span>{area}</span>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              className="quick-links"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Link to="/building-services" className="btn-secondary">Building Services</Link>
              <Link to="/property-services" className="btn-secondary">Property Services</Link>
              <Link to="/havering" className="btn-secondary">Havering</Link>
              <Link to="/contact" className="btn-secondary">Get a Quote</Link>
            </motion.div>
          </div>
        </section>

        <CTABlock />
      </PageLayout>
    </>
  );
};

export default AirConditioningPage;
